import React, { useState } from 'react';
import { Plus, Trash2, Save, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FaqItemCard({ item, index, total, token, onRefresh, onMove }) {
  const [form, setForm] = useState({ question: item.question, answer: item.answer });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Frage und Antwort dürfen nicht leer sein');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/faq/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, order: item.order }),
      });
      if (response.ok) {
        toast.success('FAQ gespeichert');
        onRefresh();
      } else {
        toast.error('Speichern fehlgeschlagen');
      }
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('FAQ-Eintrag wirklich löschen?')) return;
    const response = await fetch(`${API_URL}/api/faq/${item.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      toast.success('Eintrag gelöscht');
      onRefresh();
    } else {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-muted-foreground text-sm font-mono pt-2 shrink-0 w-8">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div className="flex-1 space-y-3">
            <Input
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Frage"
              className="bg-background border-border text-foreground font-semibold"
            />
            <Textarea
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              placeholder="Antwort"
              rows={3}
              className="bg-background border-border text-foreground"
            />
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => onMove(index, -1)} disabled={index === 0}>
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onMove(index, 1)} disabled={index === total - 1}>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
          <Button variant="ghost" size="sm" onClick={remove} className="text-red-500 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FaqAdmin({ token, items, onRefresh }) {
  const [newItem, setNewItem] = useState({ question: '', answer: '' });
  const [creating, setCreating] = useState(false);

  const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

  const create = async () => {
    if (!newItem.question.trim() || !newItem.answer.trim()) {
      toast.error('Bitte Frage und Antwort eingeben');
      return;
    }
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/faq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...newItem, order: items.length }),
      });
      if (response.ok) {
        toast.success('FAQ-Eintrag angelegt');
        setNewItem({ question: '', answer: '' });
        onRefresh();
      } else {
        toast.error('Anlegen fehlgeschlagen');
      }
    } catch {
      toast.error('Anlegen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const moveItem = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sorted.length) return;
    const a = sorted[index];
    const b = sorted[target];
    try {
      await Promise.all([
        fetch(`${API_URL}/api/faq/${a.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ question: a.question, answer: a.answer, order: target }),
        }),
        fetch(`${API_URL}/api/faq/${b.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ question: b.question, answer: b.answer, order: index }),
        }),
      ]);
      onRefresh();
    } catch {
      toast.error('Reihenfolge konnte nicht geändert werden');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg">Neuen FAQ-Eintrag anlegen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-foreground text-sm">Frage *</Label>
            <Input
              value={newItem.question}
              onChange={(e) => setNewItem({ ...newItem, question: e.target.value })}
              className="bg-background border-border text-foreground"
            />
          </div>
          <div>
            <Label className="text-foreground text-sm">Antwort *</Label>
            <Textarea
              value={newItem.answer}
              onChange={(e) => setNewItem({ ...newItem, answer: e.target.value })}
              rows={3}
              className="bg-background border-border text-foreground"
            />
          </div>
          <Button onClick={create} disabled={creating}>
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Eintrag anlegen
          </Button>
        </CardContent>
      </Card>

      {sorted.map((item, index) => (
        <FaqItemCard
          key={item.id}
          item={item}
          index={index}
          total={sorted.length}
          token={token}
          onRefresh={onRefresh}
          onMove={moveItem}
        />
      ))}

      {sorted.length === 0 && (
        <p className="text-muted-foreground text-center py-8">Noch keine FAQ-Einträge vorhanden.</p>
      )}
    </div>
  );
}
